import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import institutionService, { type InstitutionDTO } from '../services/institutionService';
import dirigenteService from '../services/dirigenteService';
import type { Dirigente } from '../types/dirigente.types';
import documentoInstitucionalService from '../services/documentoInstitucionalService';
import type { DocumentoInstitucional as DocumentoInstitucionalArquivo, TipoDocumentoInstitucional } from '../types/documentoInstitucional.types';
import {
  DOCUMENTOS_OBRIGATORIOS,
  STATUS_DOCUMENTO_LABELS,
  TIPOS_DOCUMENTO_LABELS,
} from '../types/documentoInstitucional.types';
import documentoPessoalService from '../services/documentoPessoalService';
import type { DocumentoPessoalDTO } from '../types/documentoPessoal.types';
import { buildInstitutionDocumentStatusSummary, isDocumentoAprovado, isDocumentoReprovado } from '../utils/statusOSC.utils';
import { STATUS_OSC_LABELS, type StatusOSC } from '../types/statusOSC.types';
import { formatCep, formatCnpj, formatPhoneBR } from '../utils/formatters';
import { BASENAME } from '../services/api';

type DocumentoPessoalTipoConfig = {
  tipo: string;
  nome: string;
  obrigatorio: boolean;
};

type DirigentePrintData = {
  dirigente: Dirigente;
  documentos: DocumentoPessoalDTO[];
  faltantes: string[];
  pendentes: string[];
  reprovados: string[];
};

const dateFmt = new Intl.DateTimeFormat('pt-BR');
const dateTimeFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const formatDate = (value?: string | null) => {
  if (!value) return 'Nao informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFmt.format(date);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Nao informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFmt.format(date);
};

const formatText = (value?: string | null) => {
  const text = String(value || '').trim();
  return text || 'Nao informado';
};

const normalizeCargo = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const TIPOS_DOCUMENTO_PESSOAL_PRESIDENTE: DocumentoPessoalTipoConfig[] = [
  { tipo: 'RG_PRESIDENTE', nome: 'RG', obrigatorio: true },
  { tipo: 'CNH_PRESIDENTE', nome: 'CNH', obrigatorio: true },
  { tipo: 'DOCUMENTO_FOTO_PRESIDENTE', nome: 'Documento com Foto', obrigatorio: true },
  { tipo: 'COMPROVANTE_ENDERECO_PRESIDENTE', nome: 'Comprovante de Endereco', obrigatorio: true },
  { tipo: 'CERTIDAO_TCE_PRESIDENTE', nome: 'Certidao Negativa TCE', obrigatorio: true },
];

const TIPOS_DOCUMENTO_PESSOAL_DIRIGENTE: DocumentoPessoalTipoConfig[] = [
  { tipo: 'DOCUMENTO_FOTO_DIRIGENTE', nome: 'Documento de Identificacao com Foto', obrigatorio: true },
  { tipo: 'CERTIDAO_TCE_DIRIGENTE', nome: 'Certidao Negativa TCE', obrigatorio: true },
];

const TIPOS_DOCUMENTO_PESSOAL_LABELS: Record<string, string> = Object.fromEntries(
  [...TIPOS_DOCUMENTO_PESSOAL_PRESIDENTE, ...TIPOS_DOCUMENTO_PESSOAL_DIRIGENTE].map((t) => [t.tipo, t.nome])
);

const getTiposDocumentoPessoalObrigatorios = (cargo?: string): DocumentoPessoalTipoConfig[] =>
  cargo?.toLowerCase().includes('presidente')
    ? TIPOS_DOCUMENTO_PESSOAL_PRESIDENTE
    : TIPOS_DOCUMENTO_PESSOAL_DIRIGENTE;

const getDocumentosPessoaisAtuaisPorTipo = (documentos: DocumentoPessoalDTO[]): Map<string, DocumentoPessoalDTO> => {
  const documentosPorTipo = new Map<string, DocumentoPessoalDTO>();

  documentos.forEach((documento) => {
    const existente = documentosPorTipo.get(documento.tipoDocumento);
    const dataAtual = new Date(documento.dataUpload || 0).getTime();
    const dataExistente = existente ? new Date(existente.dataUpload || 0).getTime() : 0;

    if (!existente || dataAtual >= dataExistente) {
      documentosPorTipo.set(documento.tipoDocumento, documento);
    }
  });

  return documentosPorTipo;
};

/* ── Report primitives ───────────────────────────────────────────────────── */

const DataRow: React.FC<{ label: string; value?: React.ReactNode; wide?: boolean }> = ({ label, value, wide }) => (
  <tr>
    <td className={`py-1 pr-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 align-top whitespace-nowrap${wide ? ' w-40' : ' w-32'}`}>{label}</td>
    <td className="py-1 text-[12px] text-gray-900 align-top">{value ?? '—'}</td>
  </tr>
);

const DataGrid: React.FC<{ children: React.ReactNode; cols?: 2 | 3 | 4 }> = ({ children, cols = 2 }) => (
  <div className={`grid gap-x-8 gap-y-0 ${cols === 4 ? 'grid-cols-2 print:grid-cols-4 sm:grid-cols-4' : cols === 3 ? 'grid-cols-1 sm:grid-cols-3 print:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 print:grid-cols-2'}`}>
    {children}
  </div>
);

const DataPair: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="py-1 border-b border-gray-100">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
    <div className="text-[12px] text-gray-900 mt-0.5">{value ?? '—'}</div>
  </div>
);

const SectionTitle: React.FC<{ number: string; title: string }> = ({ number, title }) => (
  <div className="print-keep-with-next mt-7 mb-2">
    <div className="flex items-baseline gap-2 border-b-2 border-gray-800 pb-1">
      <span className="text-[11px] font-bold text-gray-500">{number}</span>
      <h2 className="text-[13px] font-bold uppercase tracking-widest text-gray-900">{title}</h2>
    </div>
  </div>
);

type TableCellProps = React.ThHTMLAttributes<HTMLTableCellElement> &
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    head?: boolean;
    align?: 'left' | 'center' | 'right';
  };

const TableCell: React.FC<TableCellProps> = ({ children, head, align = 'left', className = '', ...props }) => {
  const Tag = head ? 'th' : 'td';
  const alignment = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <Tag
      className={`border border-gray-300 px-2.5 py-1.5 align-top text-[11px] whitespace-normal break-words ${alignment} ${head ? 'bg-gray-100 font-bold uppercase tracking-wide text-gray-700' : 'text-gray-900'} ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
};

const InstitutionCompliancePrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [institution, setInstitution] = useState<InstitutionDTO | null>(null);
  const [dirigentes, setDirigentes] = useState<Dirigente[]>([]);
  const [documentosInstitucionais, setDocumentosInstitucionais] = useState<DocumentoInstitucionalArquivo[]>([]);
  const [documentosDirigentes, setDocumentosDirigentes] = useState<DirigentePrintData[]>([]);

  useEffect(() => {
    if (!id) {
      setError('Instituicao nao informada.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const institutionData = await institutionService.getByInstitutionId(id);
        const dirigentesData = await dirigenteService.listar(id, false).catch(() => []);
        const documentosData = await documentoInstitucionalService.listar(id).catch(() => []);

        const dirigentesDocs = await Promise.all(
          (dirigentesData || [])
            .filter((dirigente) => Boolean(dirigente.id || dirigente.pessoaId))
            .map(async (dirigente) => {
              const dirigenteId = dirigente.pessoaId || dirigente.id;
              const documentos = dirigenteId
                ? await documentoPessoalService.listarPorDirigente(dirigenteId).catch(() => [])
                : [];
              const documentosAtuais = getDocumentosPessoaisAtuaisPorTipo(Array.isArray(documentos) ? documentos : []);
              const obrigatorios = getTiposDocumentoPessoalObrigatorios(dirigente.cargo).filter((item) => item.obrigatorio);

              const faltantes: string[] = [];
              const pendentes: string[] = [];
              const reprovados: string[] = [];

              obrigatorios.forEach((tipo) => {
                const documentoAtual = documentosAtuais.get(tipo.tipo);
                if (!documentoAtual) {
                  faltantes.push(tipo.nome);
                  return;
                }
                if (isDocumentoReprovado(documentoAtual.statusDocumento)) {
                  reprovados.push(tipo.nome);
                  return;
                }
                if (!isDocumentoAprovado(documentoAtual.statusDocumento)) {
                  pendentes.push(tipo.nome);
                }
              });

              return {
                dirigente,
                documentos: Array.isArray(documentos) ? documentos : [],
                faltantes,
                pendentes,
                reprovados,
              } satisfies DirigentePrintData;
            })
        );

        setInstitution(institutionData || null);
        setDirigentes(Array.isArray(dirigentesData) ? dirigentesData : []);
        setDocumentosInstitucionais(Array.isArray(documentosData) ? documentosData : []);
        setDocumentosDirigentes(dirigentesDocs);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Nao foi possivel carregar o impresso de conformidade.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    if (!loading && !error && searchParams.get('autoprint') === '1') {
      const timeout = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [loading, error, searchParams]);

  const documentSummary = useMemo(
    () => buildInstitutionDocumentStatusSummary(documentosInstitucionais || [], {
      statusAtual: institution?.statusOSC as StatusOSC | null | undefined,
    }),
    [documentosInstitucionais, institution?.statusOSC]
  );

  const documentosAtuais = useMemo(
    () => Array.from(documentSummary.documentosAtuaisPorTipo.values()).sort((a, b) => {
      const aLabel = TIPOS_DOCUMENTO_LABELS[a.tipoDocumento as TipoDocumentoInstitucional] || a.tipoDocumento;
      const bLabel = TIPOS_DOCUMENTO_LABELS[b.tipoDocumento as TipoDocumentoInstitucional] || b.tipoDocumento;
      return aLabel.localeCompare(bLabel, 'pt-BR');
    }),
    [documentSummary.documentosAtuaisPorTipo]
  );

  const pendenciasCadastro = useMemo(() => {
    if (!institution) return [];
    const missing: string[] = [];
    if (!String(institution.razaoSocial || '').trim()) missing.push('Razao social');
    if (!String(institution.cnpj || '').trim()) missing.push('CNPJ');
    if (!String(institution.inscricaoMunicipal || '').trim()) missing.push('Inscricao municipal');
    if (!String(institution.telefone || '').trim()) missing.push('Telefone');
    if (!String(institution.emailInstitucional || '').trim()) missing.push('E-mail institucional');
    if (!String(institution.cep || '').trim()) missing.push('CEP');
    if (!String(institution.logradouro || '').trim()) missing.push('Logradouro');
    if (!String(institution.numero || '').trim()) missing.push('Numero');
    if (!String(institution.bairro || '').trim()) missing.push('Bairro');
    if (!String(institution.cidade || '').trim()) missing.push('Cidade');
    if (!String(institution.uf || '').trim()) missing.push('UF');
    if (!String(institution.numeroRegistroConselhoMunicipal || '').trim()) missing.push('Registro no conselho municipal');
    return missing;
  }, [institution]);

  const documentosObrigatoriosPendentes = useMemo(
    () => documentSummary.tiposObrigatoriosPendentes.map((tipo) => TIPOS_DOCUMENTO_LABELS[tipo] || tipo),
    [documentSummary.tiposObrigatoriosPendentes]
  );

  const resumoDirigentes = useMemo(
    () => documentosDirigentes.reduce((acc, item) => ({
      faltantes: acc.faltantes + item.faltantes.length,
      pendentes: acc.pendentes + item.pendentes.length,
      reprovados: acc.reprovados + item.reprovados.length,
      documentos: acc.documentos + item.documentos.length,
    }), { faltantes: 0, pendentes: 0, reprovados: 0, documentos: 0 }),
    [documentosDirigentes]
  );

  if (loading) {
    return <div className="min-h-screen bg-slate-50 px-4 py-10 text-center text-slate-600">Carregando impresso de conformidade...</div>;
  }

  if (error || !institution) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-red-700">Erro ao gerar impresso</p>
          <p className="mt-2 text-sm text-slate-600">{error || 'Instituicao nao encontrada.'}</p>
          <button type="button" onClick={() => navigate(-1)} className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200 print:bg-white">
      <style>{`
        @page { size: A4 portrait; margin: 18mm 16mm; }
        @media print {
          .print-hidden { display: none !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; padding: 0 !important; }
          .print-keep-together { break-inside: avoid-page !important; page-break-inside: avoid !important; }
          .print-keep-with-next { break-after: avoid-page !important; page-break-after: avoid !important; }
          .print-list-item { break-inside: avoid-page !important; page-break-inside: avoid !important; }
          tr { break-inside: avoid-page !important; page-break-inside: avoid !important; }
          thead { display: table-header-group; }
          body { background: #fff !important; }
        }
      `}</style>

      {/* Barra de ações — somente tela */}
      <div className="print-hidden sticky top-0 z-10 border-b border-gray-300 bg-white shadow-sm">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">Relatorio de Conformidade</p>
            <p className="text-xs text-gray-500">{formatText(institution.razaoSocial)}</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => navigate(-1)} className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Voltar</button>
            <button type="button" onClick={() => window.print()} className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700">Imprimir</button>
          </div>
        </div>
      </div>

      <main className="print-sheet mx-auto my-8 max-w-4xl bg-white px-10 py-10 shadow-lg print:my-0 print:shadow-none">

        {/* ── Cabeçalho ────────────────────────────────────────────────────── */}
        <header className="border-b-2 border-gray-900 pb-4">
          <div className="flex items-center gap-4">
            <img src={`${BASENAME || ''}/favicon.ico`} alt="Brasao" className="h-14 w-14 object-contain" />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Prefeitura Municipal de Sinop — MT</p>
              <p className="text-[11px] text-gray-500">Sistema de Gestao de Emendas Municipais — SIGEM</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Emitido em</p>
              <p className="text-[11px] font-semibold text-gray-700">{formatDateTime(new Date().toISOString())}</p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">Relatorio de Conformidade Institucional</p>
            <h1 className="mt-1 text-[22px] font-black uppercase tracking-wide text-gray-900">{formatText(institution.razaoSocial)}</h1>
            <p className="mt-0.5 text-[12px] text-gray-500">CNPJ {institution.cnpj ? formatCnpj(institution.cnpj) : '—'}</p>
          </div>
        </header>

        {/* ── Sumário executivo ─────────────────────────────────────────────── */}
        <div className="mt-4 grid grid-cols-4 gap-0 border border-gray-300">
          {[
            { label: 'Situacao OSC', value: STATUS_OSC_LABELS[(institution.statusOSC || documentSummary.status) as StatusOSC] || formatText(String(institution.statusOSC || documentSummary.status)) },
            { label: 'Docs. Protocolados', value: String(documentosAtuais.length) },
            { label: 'Obrigatorios Pendentes', value: String(documentosObrigatoriosPendentes.length) },
            { label: 'Dirigentes c/ Pendencias', value: String(documentosDirigentes.filter(i => i.faltantes.length || i.pendentes.length || i.reprovados.length).length) },
          ].map((item, i) => (
            <div key={i} className={`px-3 py-2 ${i < 3 ? 'border-r border-gray-300' : ''}`}>
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{item.label}</p>
              <p className="mt-0.5 text-[13px] font-bold text-gray-900">{item.value}</p>
            </div>
          ))}
        </div>

        {/* ── 1. Dados da Instituicao ───────────────────────────────────────── */}
        <SectionTitle number="1" title="Dados da Instituicao" />
        <div className="grid grid-cols-2 gap-x-10">
          <table className="w-full">
            <tbody>
              <DataRow label="Razao Social" value={formatText(institution.razaoSocial)} />
              <DataRow label="Nome Fantasia" value={formatText(institution.nomeFantasia)} />
              <DataRow label="CNPJ" value={institution.cnpj ? formatCnpj(institution.cnpj) : '—'} />
              <DataRow label="Inscr. Municipal" value={formatText(institution.inscricaoMunicipal)} />
              <DataRow label="Registro Conselho" value={formatText(institution.numeroRegistroConselhoMunicipal)} />
            </tbody>
          </table>
          <table className="w-full">
            <tbody>
              <DataRow label="Telefone" value={institution.telefone ? formatPhoneBR(institution.telefone) : '—'} />
              <DataRow label="E-mail" value={formatText(institution.emailInstitucional)} />
              <DataRow label="Endereco" value={[institution.logradouro, institution.numero, institution.bairro].filter(Boolean).join(', ') || '—'} />
              <DataRow label="CEP" value={institution.cep ? formatCep(institution.cep) : '—'} />
              <DataRow label="Cidade / UF" value={`${formatText(institution.cidade)} / ${formatText(institution.uf)}`} />
            </tbody>
          </table>
        </div>

        {/* ── 2. Resumo de Conformidade ─────────────────────────────────────── */}
        <SectionTitle number="2" title="Resumo de Conformidade" />
        <table className="w-full">
          <tbody>
            <tr>
              <td className="py-1 pr-8 text-[11px]"><span className="font-semibold uppercase tracking-wide text-gray-500">Documentos aprovados: </span><span className="text-gray-900">{documentSummary.dashboard.aprovados}</span></td>
              <td className="py-1 pr-8 text-[11px]"><span className="font-semibold uppercase tracking-wide text-gray-500">Pendentes de analise: </span><span className="text-gray-900">{documentSummary.dashboard.pendentes + resumoDirigentes.pendentes}</span></td>
              <td className="py-1 pr-8 text-[11px]"><span className="font-semibold uppercase tracking-wide text-gray-500">Reprovados: </span><span className="text-gray-900">{documentSummary.dashboard.reprovados + resumoDirigentes.reprovados}</span></td>
              <td className="py-1 text-[11px]"><span className="font-semibold uppercase tracking-wide text-gray-500">% Obrigatorios: </span><span className="text-gray-900 font-bold">{documentSummary.dashboard.percentualCompleto}%</span></td>
            </tr>
          </tbody>
        </table>

        {/* ── 3. Pendencias Identificadas ──────────────────────────────────── */}
        <SectionTitle number="3" title="Pendencias Identificadas" />
        <div className="grid grid-cols-2 gap-x-10">
          <div className="print-keep-together">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">Cadastro da Instituicao</p>
            {pendenciasCadastro.length > 0 ? (
              <ul className="list-disc pl-4 text-[11px] text-gray-800 space-y-0.5">
                {pendenciasCadastro.map((item) => <li key={item} className="print-list-item">{item}</li>)}
              </ul>
            ) : (
              <p className="text-[11px] text-gray-700">Nenhuma pendencia cadastral.</p>
            )}
          </div>
          <div className="print-keep-together">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">Documentos Obrigatorios Faltando</p>
            {documentosObrigatoriosPendentes.length > 0 ? (
              <ul className="list-disc pl-4 text-[11px] text-gray-800 space-y-0.5">
                {documentosObrigatoriosPendentes.map((item) => <li key={item} className="print-list-item">{item}</li>)}
              </ul>
            ) : (
              <p className="text-[11px] text-gray-700">Todos os documentos obrigatorios foram protocolados.</p>
            )}
          </div>
        </div>

        {/* ── 4. Documentos Institucionais ─────────────────────────────────── */}
        <SectionTitle number="4" title="Documentos Institucionais Protocolados" />
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '30%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '13%' }} />
          </colgroup>
          <thead>
            <tr>
              <TableCell head>Documento</TableCell>
              <TableCell head align="center">Situacao</TableCell>
              <TableCell head align="center">N° Documento</TableCell>
              <TableCell head>Arquivo</TableCell>
              <TableCell head align="center">Validade</TableCell>
            </tr>
          </thead>
          <tbody>
            {documentosAtuais.map((doc) => (
              <tr key={doc.id}>
                <TableCell>{TIPOS_DOCUMENTO_LABELS[doc.tipoDocumento as TipoDocumentoInstitucional] || doc.tipoDocumento}</TableCell>
                <TableCell align="center">{STATUS_DOCUMENTO_LABELS[doc.statusDocumento as keyof typeof STATUS_DOCUMENTO_LABELS] || formatText(doc.statusDocumento)}</TableCell>
                <TableCell align="center">{doc.numeroDocumento || '—'}</TableCell>
                <TableCell className="text-[10px]">{doc.nomeOriginal}</TableCell>
                <TableCell align="center">{doc.dataValidade ? formatDate(doc.dataValidade) : '—'}</TableCell>
              </tr>
            ))}
            {documentosAtuais.length === 0 && (
              <tr><TableCell colSpan={5}>Nenhum documento institucional protocolado.</TableCell></tr>
            )}
          </tbody>
        </table>

        {/* ── 5. Dirigentes ────────────────────────────────────────────────── */}
        <SectionTitle number="5" title="Dirigentes e Documentos Pessoais" />
        <div className="space-y-6">
          {documentosDirigentes.map((item, index) => (
            <div key={item.dirigente.id || index} className="print-keep-together">
              {/* Cabeçalho do dirigente */}
              <div className="flex items-baseline gap-6 border-b border-gray-400 pb-1 mb-2">
                <span className="text-[12px] font-bold text-gray-900">{formatText(item.dirigente.nomeCompleto)}</span>
                <span className="text-[11px] text-gray-500">{formatText(item.dirigente.cargo)}</span>
                <span className="ml-auto text-[10px] text-gray-400">CPF {formatText(item.dirigente.cpf)}</span>
              </div>
              {/* Tabela de documentos */}
              <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '44%' }} />
                  <col style={{ width: '14%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <TableCell head>Documento</TableCell>
                    <TableCell head align="center">Situacao</TableCell>
                    <TableCell head>Arquivo</TableCell>
                    <TableCell head align="center">Validade</TableCell>
                  </tr>
                </thead>
                <tbody>
                  {item.documentos.map((doc) => (
                    <tr key={doc.id}>
                      <TableCell>{TIPOS_DOCUMENTO_PESSOAL_LABELS[doc.tipoDocumento] || formatText(doc.tipoDocumento)}</TableCell>
                      <TableCell align="center">{STATUS_DOCUMENTO_LABELS[doc.statusDocumento as keyof typeof STATUS_DOCUMENTO_LABELS] || formatText(doc.statusDocumento)}</TableCell>
                      <TableCell className="text-[10px]">{doc.nomeOriginal}</TableCell>
                      <TableCell align="center">{doc.dataValidade ? formatDate(doc.dataValidade) : '—'}</TableCell>
                    </tr>
                  ))}
                  {item.documentos.length === 0 && (
                    <tr><TableCell colSpan={4}>Nenhum documento pessoal protocolado.</TableCell></tr>
                  )}
                </tbody>
              </table>
              {/* Pendencias */}
              {(item.faltantes.length > 0 || item.pendentes.length > 0 || item.reprovados.length > 0) && (
                <div className="mt-1 flex gap-6 text-[10px] text-gray-600">
                  {item.faltantes.length > 0 && <span><strong>Faltando:</strong> {item.faltantes.join(', ')}</span>}
                  {item.pendentes.length > 0 && <span><strong>Pendentes:</strong> {item.pendentes.join(', ')}</span>}
                  {item.reprovados.length > 0 && <span><strong>Reprovados:</strong> {item.reprovados.join(', ')}</span>}
                </div>
              )}
            </div>
          ))}
          {documentosDirigentes.length === 0 && (
            <p className="text-[11px] text-gray-600">Nenhum dirigente cadastrado para esta instituicao.</p>
          )}
        </div>

        {/* ── 6. Observacao Final ──────────────────────────────────────────── */}
        <SectionTitle number="6" title="Observacao Final" />
        <p className="text-[11px] leading-5 text-gray-700">
          Este relatorio consolida o cadastro da instituicao, os documentos protocolados e as pendencias identificadas no momento da emissao.
          A situacao apresentada reflete o estado atual registrado no sistema na data indicada no cabecalho.
        </p>

        {/* Rodapé */}
        <div className="mt-10 border-t border-gray-300 pt-3 flex justify-between items-center">
          <p className="text-[9px] text-gray-400">SIGEM — Sistema de Gestao de Emendas Municipais · Prefeitura Municipal de Sinop - MT</p>
          <p className="text-[9px] text-gray-400">Documento gerado automaticamente em {formatDateTime(new Date().toISOString())}</p>
        </div>

      </main>
    </div>
  );
};

export default InstitutionCompliancePrintPage;
